import {
    FunctionComponent,
    HTMLProps,
    memo,
    MouseEventHandler,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    WheelEventHandler,
} from "react"
import * as Icons from "@nova/component-icons"
import { Divider } from "components/Divider"
import { GetMediaControls, MediaControls, MediaListener } from "./ColorFinderFootageDisplay"
import { IconButton } from "@nova/component-btn"
import classNames from "classnames"
import { ColorFilter } from "./ColorFinderPanel"
import { statusToIcon } from "./ColorFinderFeed"
import { Comment } from "@nova/schemas"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { setCurrentColorFinderCommentIndex } from "./redux/slice"
import {
    selectColorFinderAnomalySensitivity,
    selectColorFinderView,
    selectCurrentColorFinderComment,
} from "./redux/selectors"
import { TimelineScrollbar, TimelineScrollbarControls } from "./TimelineScrollbar"
import { ColorFinderTiler } from "./ColorFinderTiler.class"
import { selectIconsByTargetId } from "features/entity-caching/icons/selectors"
import { ImageColorFinderFlight, VideoColorFinderFlight } from "@nova/protobuf"
import { parseIconTargetPayload } from "plugins/IconTargetPayload"
import { getIconUrl } from "@nova/component-map-icons"
import { decodeNumberToColor } from "@nova/serializer/src/client/util"
import { defaultIconColor } from "features/map/layers/icons/constants"
import { Dropdown } from "@nova/component-dropdown"
import { clearTimelineCache, drawTimelineLegend, drawTimelineV2 } from "./drawTimelineV2"
import { Scrubbable } from "./Scrubbable"
import { useOnHold } from "panels/controls/useOnHold"
import { drawAnomalyTimeline } from "./anomalies/drawAnomalyTimeline"

interface ColorTimelineProps {
    getControls: () => MediaControls | null
    filter: ColorFilter
    comments: Comment<"cf-frame">[]
    tiler: ColorFinderTiler
    flight: ImageColorFinderFlight | VideoColorFinderFlight
    anomalyIndex: Uint8Array | undefined
}

const findNearestCommentIndex = (comments: Comment<"cf-frame">[], targetFrame: number) => {
    let low = 0
    let high = comments.length - 1
    let resultIndex = -1

    while (low <= high) {
        const mid = low + Math.round((high - low) / 2)

        if (comments[mid].context.frame <= targetFrame) {
            resultIndex = mid
            low = mid + 1
        } else {
            high = mid - 1
        }
    }
    return resultIndex
}

export function ColorFinderTimeline({
    getControls,
    filter,
    comments,
    tiler,
    flight,
    anomalyIndex,
}: ColorTimelineProps) {
    const dispatch = useAppDispatch()

    const timeDisplayRef = useRef<HTMLInputElement>(null)
    const frameDisplayRef = useRef<HTMLInputElement>(null)

    const currentCommentIndex = useAppSelector(selectCurrentColorFinderComment)
    const view = useAppSelector(selectColorFinderView)

    const [isOpen, setIsOpen] = useState(true)
    const [isPlaying, setIsPlaying] = useState(false)
    const [isVideo, setIsVideo] = useState<boolean>()
    const [playbackSpeed, setPlaybackSpeed] = useState(1)

    useLayoutEffect(() => {
        if (view === "fullscreen") {
            setIsOpen(false)
        } else {
            setIsOpen(true)
        }
    }, [view])

    useEffect(() => {
        const controls = getControls()
        if (!controls) return

        const mediaListener: MediaListener = ({ frame, currentTime, isPlaying, playbackSpeed }) => {
            setIsPlaying(isPlaying)
            setIsVideo(controls.type === "video")
            setPlaybackSpeed(playbackSpeed)

            if (timeDisplayRef.current && timeDisplayRef.current !== document.activeElement) {
                timeDisplayRef.current.value = formatTime(currentTime)
            }

            if (frameDisplayRef.current) {
                frameDisplayRef.current.value = `${frame + 1}/${tiler.getNumFrames()}`
            }
        }

        const currentState = controls.getState()
        if (currentState) {
            mediaListener(currentState)
        }

        controls.subscribe(mediaListener)

        return () => controls.unsubscribe(mediaListener)
    }, [comments, getControls, currentCommentIndex, dispatch, tiler])

    const handlePreviousFrame = () => {
        const controls = getControls()
        if (!controls) return
        const currentFrame = controls.getFrame()
        if (currentFrame <= 0) return currentFrame
        controls.setFrame(currentFrame - 1)
    }

    const handleNextFrame = () => {
        const controls = getControls()
        if (!controls) return
        const currentFrame = controls.getFrame()
        if (currentFrame >= tiler.getNumFrames()) return currentFrame
        controls.setFrame(currentFrame + 1)
    }

    const handleTimeChange = () => {
        const controls = getControls()
        if (!timeDisplayRef.current || !controls) return

        const newTime = parseTime(timeDisplayRef.current.value)
        controls.setCurrentTime(newTime)
    }

    const handleFrameChange = () => {
        const controls = getControls()
        if (!frameDisplayRef.current || !controls) return

        const newFrame = parseInputFrame(frameDisplayRef.current.value)
        controls.setFrame(newFrame - 1)
    }

    const prevButtonProps = useOnHold(handlePreviousFrame)
    const nextButtonProps = useOnHold(handleNextFrame)

    return (
        <div className="flex flex-col divide-y divide-white-200 border border-white-200 rounded-lg overflow-hidden">
            <div className="flex bg-white-100 gap-1 p-1 items-center min-h-[32px] max-h-fit h-full">
                <IconButton onClick={() => setIsOpen((prev) => !prev)} className="text-gray-500">
                    <Icons.SmallIcon
                        Icon={isOpen ? Icons.KeyboardDoubleArrowDown : Icons.KeyboardDoubleArrowUp}
                    />
                </IconButton>
                <small>Timeline</small>

                <Divider vertical />

                <div className="flex items-center gap-1">
                    {isVideo && (
                        <IconButton
                            onClick={() => {
                                if (isPlaying) {
                                    getControls()?.pause()
                                } else {
                                    getControls()?.play()
                                }
                            }}
                        >
                            <Icons.SmallIcon Icon={isPlaying ? Icons.Pause : Icons.Play} />
                        </IconButton>
                    )}

                    <IconButton {...prevButtonProps}>
                        <Icons.SmallIcon Icon={Icons.FirstPage} />
                    </IconButton>

                    <IconButton {...nextButtonProps}>
                        <Icons.SmallIcon Icon={Icons.LastPage} />
                    </IconButton>
                </div>

                <Divider vertical />

                {isVideo && (
                    <Dropdown>
                        <Dropdown.TriggerButton withoutDropdownArrow>
                            <div className="text-gray-100">{playbackSpeed}x</div>
                        </Dropdown.TriggerButton>
                        <Dropdown.Menu
                            position={isOpen ? "bottom-left" : "top-left"}
                            className="text-white text-[12px]"
                        >
                            <Dropdown.ButtonItem
                                onClick={() => {
                                    getControls()?.setPlaybackSpeed(0.25)
                                }}
                            >
                                0.25x
                            </Dropdown.ButtonItem>
                            <Dropdown.ButtonItem
                                onClick={() => {
                                    getControls()?.setPlaybackSpeed(0.5)
                                }}
                            >
                                0.5x
                            </Dropdown.ButtonItem>
                            <Dropdown.ButtonItem
                                onClick={() => {
                                    getControls()?.setPlaybackSpeed(1)
                                }}
                            >
                                1x
                            </Dropdown.ButtonItem>
                            <Dropdown.ButtonItem
                                onClick={() => {
                                    getControls()?.setPlaybackSpeed(2)
                                }}
                            >
                                2x
                            </Dropdown.ButtonItem>
                        </Dropdown.Menu>
                    </Dropdown>
                )}

                <form
                    onSubmit={(e) => {
                        e.preventDefault()
                        timeDisplayRef.current?.blur()
                        if (isVideo) {
                            handleTimeChange()
                        } else {
                            handleFrameChange()
                        }
                    }}
                >
                    {isVideo && (
                        <input
                            name="time"
                            type="text"
                            ref={timeDisplayRef}
                            className="mono text-blue-500 bg-transparent border-none w-28 text-[12px]"
                            onKeyDown={(e) => e.stopPropagation()}
                        />
                    )}
                    {!isVideo && (
                        <input
                            name="frame"
                            type="text"
                            ref={frameDisplayRef}
                            className="mono text-blue-500 bg-transparent border-none w-fit text-[12px]"
                            onKeyDown={(e) => e.stopPropagation()}
                        />
                    )}
                </form>
                <small className="mono text-blue-500 bg-transparent border-none" />

                {isVideo && !isOpen && (
                    <div className="flex-1">
                        <ColorFinderTrack
                            getMediaControls={getControls}
                            numFrames={tiler.getNumFrames()}
                        />
                    </div>
                )}
            </div>
            <div>
                {isOpen && (
                    <ColorFinderTimelineBody
                        anomalyIndex={anomalyIndex}
                        key="woo"
                        comments={comments}
                        filter={filter}
                        getControls={getControls}
                        tiler={tiler}
                        flight={flight}
                    />
                )}
            </div>
        </div>
    )
}

interface ColorFinderTrackProps {
    getMediaControls: GetMediaControls
    numFrames: number
}

const ColorFinderTrack: FunctionComponent<ColorFinderTrackProps> = ({
    getMediaControls,
    numFrames,
}) => {
    const scrubberRef = useRef<HTMLDivElement>(null)
    const timePassedRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const mediaControls = getMediaControls()
        if (!mediaControls) return

        const listener: MediaListener = ({ frame }) => {
            const scrubber = scrubberRef.current
            const timePassed = timePassedRef.current
            if (!scrubber || !timePassed) return
            const position = getRelativeHeadPosition(frame, numFrames)
            scrubber.style.left = `${position}%`
            timePassed.style.width = `${position}%`
        }

        const currentState = mediaControls.getState()
        if (currentState) {
            listener(currentState)
        }

        mediaControls.subscribe(listener)
        return () => mediaControls.unsubscribe(listener)
    }, [getMediaControls, numFrames])

    const handleGraphClick = (e: number) => {
        const frame = Math.round(e * numFrames)
        getMediaControls()?.setFrame(frame)
    }

    return (
        <Scrubbable onScrub={handleGraphClick}>
            <div className="relative">
                <div className="h-1 bg-gray-500 rounded-lg hover:h-1.5 overflow-hidden cursor-pointer">
                    <div className="bg-[#D9D9D9] h-full" ref={timePassedRef} />
                </div>
                {scrubber({ ref: scrubberRef })}
            </div>
        </Scrubbable>
    )
}

const ColorFinderTimelineBody: FunctionComponent<ColorTimelineProps> = memo(
    ({ comments, filter, getControls, tiler, flight, anomalyIndex }) => {
        const dispatch = useAppDispatch()

        const canvasRef = useRef<HTMLCanvasElement>(null)
        const anomalyTimelineRef = useRef<HTMLCanvasElement>(null)
        const commentsRowRef = useRef<HTMLDivElement>(null)
        const iconsRowRef = useRef<HTMLDivElement>(null)
        const scrubberRef = useRef<HTMLDivElement>(null)
        const icons = useAppSelector(selectIconsByTargetId(flight.id))
        const scrollbarRef = useRef<TimelineScrollbarControls>(null)
        const anomalySensitivity = useAppSelector(selectColorFinderAnomalySensitivity)

        const currentCommentIndex = useAppSelector(selectCurrentColorFinderComment)

        const imageData = useRef(new ImageData(1, 1))
        const anomalyImageData = useRef(new ImageData(1, 1))

        const timelineViewport = useRef({ left: 0, right: 100 })

        const draw = useCallback(() => {
            const canvas = canvasRef.current
            if (!canvas || !tiler) return

            const width = canvas.getBoundingClientRect().width
            if (!width) return

            tiler.setWidth(width)

            drawTimelineV2(
                tiler,
                Math.round(tiler.getNumFrames() * (timelineViewport.current.left / 100)),
                Math.round(tiler.getNumFrames() * (timelineViewport.current.right / 100)),
                canvas,
                filter,
                imageData.current
            )
        }, [filter, tiler])

        const drawAnomaly = useCallback(() => {
            const canvas = anomalyTimelineRef.current
            if (!canvas || !anomalyIndex) return

            drawAnomalyTimeline(
                tiler,
                anomalyIndex,
                Math.round(tiler.getNumFrames() * (timelineViewport.current.left / 100)),
                Math.round(tiler.getNumFrames() * (timelineViewport.current.right / 100)),
                canvas,
                anomalyImageData.current,
                anomalySensitivity
            )
        }, [anomalyIndex, anomalySensitivity, tiler])

        const placeScrubber = useCallback(() => {
            const currentFrame = getControls()?.getFrame()
            if (currentFrame && scrubberRef.current) {
                const position = getRelativeHeadPosition(
                    currentFrame,
                    tiler.getNumFrames(),
                    timelineViewport.current.left,
                    timelineViewport.current.right
                )
                scrubberRef.current.style.left = `${position}%`
            }
        }, [getControls, tiler])

        useEffect(() => {
            const onResize = () => {
                const mainCanvas = canvasRef.current
                const anomalyCanvas = anomalyTimelineRef.current
                if (!mainCanvas || !anomalyCanvas) return

                const width = mainCanvas.getBoundingClientRect().width
                if (!width) return

                mainCanvas.width = width
                mainCanvas.height = 320

                anomalyCanvas.width = width
                anomalyCanvas.height = 24

                tiler?.setWidth(width)

                imageData.current = new ImageData(width, 320)
                anomalyImageData.current = new ImageData(width, 24)

                draw()
                drawAnomaly()
                placeScrubber()
            }
            onResize()
            onResize()

            window.addEventListener("resize", onResize)
            const tileLoadListener = () => {
                draw()
                drawAnomaly()
                placeScrubber()
            }
            tiler.addTileLoadListener(tileLoadListener)

            return () => {
                window.removeEventListener("resize", onResize)
                tiler.removeTileLoadListener(tileLoadListener)
            }
        }, [draw, drawAnomaly, placeScrubber, tiler])

        useEffect(() => {
            const mediaListener: MediaListener = ({ frame }) => {
                if (!scrubberRef.current) return
                const position = getRelativeHeadPosition(
                    frame,
                    tiler.getNumFrames(),
                    timelineViewport.current.left,
                    timelineViewport.current.right
                )
                scrubberRef.current.style.left = `${position}%`

                if (comments.length) {
                    const foundIndex = findNearestCommentIndex(comments, frame)
                    if (foundIndex !== currentCommentIndex)
                        dispatch(setCurrentColorFinderCommentIndex(foundIndex))
                }
            }

            getControls()?.subscribe(mediaListener)

            return () => getControls()?.unsubscribe(mediaListener)
        }, [comments, currentCommentIndex, dispatch, getControls, tiler])

        useEffect(() => {
            setTimeout(() => {
                clearTimelineCache()
                draw()
            }, 0)

            return () => clearTimelineCache()
        }, [filter, draw])

        const handleGraphClick: MouseEventHandler<HTMLCanvasElement> = (e) => {
            const frame = frameForClick(
                e,
                tiler.getNumFrames(),
                timelineViewport.current.left,
                timelineViewport.current.right
            )
            getControls()?.setFrame(frame)
        }

        const handleGraphDrag: MouseEventHandler<HTMLCanvasElement> = (e) => {
            if (e.buttons === 1) {
                const frame = frameForClick(
                    e,
                    tiler.getNumFrames(),
                    timelineViewport.current.left,
                    timelineViewport.current.right
                )
                getControls()?.setFrame(frame)
            } else if (e.buttons === 4) {
                e.preventDefault()

                const rect = e.currentTarget.getBoundingClientRect()
                const viewportWidth = timelineViewport.current.right - timelineViewport.current.left

                const pixelRatio = -e.movementX / rect.width

                const globalDelta = pixelRatio * viewportWidth

                scrollbarRef.current?.move(globalDelta)
            }
        }

        const handleTimelineScroll: WheelEventHandler<HTMLDivElement> = (e) => {
            const percent =
                (e.nativeEvent.offsetX / e.currentTarget.getBoundingClientRect().width) * 100
            scrollbarRef.current?.zoom(percent, e.deltaY < 0)
        }

        return (
            <div>
                <div className={classNames("flex divide-x divide-white-200")}>
                    {/* Legend Column */}
                    <div>
                        <div className="h-36">
                            <ColorFinderTimelineLegend />
                        </div>
                        <div className="p-1 h-6">
                            <Icons.ControlCamera />
                        </div>

                        {!!comments.length && (
                            <div className="text-white-400 h-6 flex items-center justify-center">
                                <Icons.ExtraSmallIcon Icon={Icons.Chat} />
                            </div>
                        )}

                        {!!icons.length && (
                            <div className="text-white-400 h-6 flex items-center justify-center">
                                <Icons.ExtraSmallIcon Icon={Icons.PinDrop} />
                            </div>
                        )}
                    </div>

                    {/* Data Column */}
                    <div className="flex-1 max-w-full w-full">
                        <div
                            className="h-36 relative w-full overflow-x-clip"
                            onWheel={handleTimelineScroll}
                        >
                            <canvas
                                id="timeline"
                                ref={canvasRef}
                                onClick={handleGraphClick}
                                onMouseMove={handleGraphDrag}
                                className="bg-[#202020] w-full h-full"
                            />
                            {scrubber({ ref: scrubberRef })}
                        </div>
                        <div className="overflow-x-hidden divide-y divide-white-200">
                            <div className="h-6">
                                <canvas
                                    ref={anomalyTimelineRef}
                                    onClick={handleGraphClick}
                                    onMouseMove={handleGraphDrag}
                                    className="h-full w-full"
                                />
                            </div>
                            {!!comments.length && (
                                <div ref={commentsRowRef} className="h-[29px] relative w-full">
                                    {comments.map((comment, i) => {
                                        return (
                                            <button
                                                className={classNames(
                                                    "absolute bg-opacity-100 hover:bg-gray-500 hover:opacity-100 hover:z-10 cursor-pointer rounded-full flex items-center p-1",
                                                    i === currentCommentIndex + 1 && "z-10"
                                                )}
                                                key={comment.id}
                                                style={{
                                                    left: `${(comment.context.frame / tiler.getNumFrames()) * 100}%`,
                                                    transform: "translateX(-50%)",
                                                }}
                                                onClick={() =>
                                                    getControls()?.setFrame(comment.context.frame)
                                                }
                                            >
                                                {statusToIcon(comment.context.status)}
                                            </button>
                                        )
                                    })}
                                </div>
                            )}

                            {!!icons.length && (
                                <div ref={iconsRowRef} className="h-6 relative w-full">
                                    {icons.map((icon, i) => {
                                        const payload = parseIconTargetPayload<"cf-frame">(
                                            icon.targetPayload
                                        )

                                        if (!payload) return null

                                        return (
                                            <button
                                                className={classNames(
                                                    "absolute bg-opacity-100 hover:bg-gray-500 hover:opacity-100 hover:z-10 cursor-pointer rounded-full flex items-center p-1",
                                                    i === currentCommentIndex + 1 && "z-10"
                                                )}
                                                key={icon.id}
                                                style={{
                                                    left: `${(payload.frame / tiler.getNumFrames()) * 100}%`,
                                                    transform: "translateX(-50%)",
                                                }}
                                                onClick={() =>
                                                    getControls()?.setFrame(payload.frame)
                                                }
                                            >
                                                <svg
                                                    data-src={getIconUrl(icon.url)}
                                                    style={{
                                                        color: icon.color
                                                            ? decodeNumberToColor(icon.color) ||
                                                              defaultIconColor
                                                            : undefined,
                                                        fill: icon.color
                                                            ? decodeNumberToColor(icon.color) ||
                                                              defaultIconColor
                                                            : undefined,
                                                    }}
                                                    height="1rem"
                                                    width="1rem"
                                                />
                                            </button>
                                        )
                                    })}
                                </div>
                            )}
                            <div>
                                <TimelineScrollbar
                                    scrollbarRef={scrollbarRef}
                                    onBallMove={(v) => {
                                        const { pos } = v
                                        timelineViewport.current = pos

                                        const viewportWidthPercent = Math.max(
                                            0.1,
                                            pos.right - pos.left
                                        )
                                        const scale = 100 / viewportWidthPercent

                                        const newWidth = scale * 100
                                        const newLeft = -pos.left * scale

                                        const transformableRows = [commentsRowRef, iconsRowRef]
                                        draw()
                                        drawAnomaly()
                                        placeScrubber()
                                        for (const ref of transformableRows) {
                                            if (ref.current) {
                                                ref.current.style.width = `${newWidth}%`
                                                ref.current.style.left = `${newLeft}%`

                                                ref.current.style.transform = ""
                                                ref.current.style.transformOrigin = ""
                                            }
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
)

ColorFinderTimelineBody.displayName = "ColorFinderTimelineBody"

const scrubber = (props: HTMLProps<HTMLDivElement>) => (
    <div
        {...props}
        className="top-0 h-full absolute items-center pointer-events-none group/scrubber"
    >
        <div className="h-0 w-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[6px] border-t-gray-50 rounded-sm pointer-events-auto absolute -top-1 z-50 -translate-x-1/2 left-[0.5px] group-hover/scrubber:scale-125"></div>
        <div className="w-px h-full bg-blue-500 flex flex-col justify-center" />
    </div>
)

const ColorFinderTimelineLegend = () => {
    const legendRef = useRef<HTMLDivElement>(null)
    useLayoutEffect(() => {
        const legend = legendRef.current
        if (!legend) return

        drawTimelineLegend(legend)
    }, [])

    return <div ref={legendRef} className="flex gap-0.5 pb-0 py-0.5 px-1 flex-col h-full" />
}

function formatTime(totalSeconds: number): string {
    if (!Number.isFinite(totalSeconds) || totalSeconds < 0) {
        return "00:00:00"
    }

    const total = Math.floor(totalSeconds)
    const hours = Math.floor(total / 3600)
    const minutes = Math.floor((total % 3600) / 60)
    const seconds = total % 60

    const pad = (n: number) => String(n).padStart(2, "0")

    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
}

function parseTime(input: string): number {
    // Strip all non-numeric characters
    const digits = input.replace(/\D/g, "")

    if (digits.length === 0) return 0

    // Slice from right-to-left
    const s = parseInt(digits.slice(-2) || "0", 10)
    const m = parseInt(digits.slice(-4, -2) || "0", 10)
    const h = parseInt(digits.slice(0, -4) || "0", 10)

    return h * 3600 + m * 60 + s
}

function parseInputFrame(input: string): number {
    const digits = +input.split("/")[0].replace(/\D/g, "")

    if (isNaN(digits)) return 0

    return digits
}

function mapValueToRange(
    value: number,
    fromMin: number,
    fromMax: number,
    toMin: number,
    toMax: number
): number {
    const percent: number = (value - fromMin) / (fromMax - fromMin)
    const offset: number = percent * (toMax - toMin)
    return toMin + offset
}

function getRelativeHeadPosition(
    currentFrame: number,
    totalFrames: number,
    scrubberLeft: number = 0,
    scrubberRight: number = 100
): number {
    const startFrameOfView: number = totalFrames * (scrubberLeft / 100)
    const endFrameOfView: number = totalFrames * (scrubberRight / 100)

    const startPosition: number = 0
    const endPosition: number = 100

    const headPosition: number = mapValueToRange(
        currentFrame,
        startFrameOfView,
        endFrameOfView,
        startPosition,
        endPosition
    )

    return headPosition
}

function getFrameFromRelativePosition(
    relativeHeadPosition: number,
    totalFrames: number,
    scrubberLeft: number,
    scrubberRight: number
): number {
    const startPosition: number = 0
    const endPosition: number = 100

    const startFrameOfView: number = totalFrames * (scrubberLeft / 100)
    const endFrameOfView: number = totalFrames * (scrubberRight / 100)

    const targetFrame: number = mapValueToRange(
        relativeHeadPosition,
        startPosition,
        endPosition,
        startFrameOfView,
        endFrameOfView
    )

    return Math.round(targetFrame)
}
const frameForClick = (
    e: React.MouseEvent<HTMLElement>,
    numFrames: number,
    viewportStart = 0,
    viewportEnd = 100
): number => {
    const scrubberElement = e.currentTarget
    // getBoundingClientRect is relative to the viewport
    const scrubberRect = scrubberElement.getBoundingClientRect()

    const clickX = e.clientX - scrubberRect.left
    const scrubberWidth = scrubberRect.width

    const relativeHeadPosition = Math.max(0, Math.min(100, (clickX / scrubberWidth) * 100))

    const targetFrame = getFrameFromRelativePosition(
        relativeHeadPosition,
        numFrames,
        viewportStart,
        viewportEnd
    )

    return targetFrame
}
